import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Archive, ArchiveRestore, Ban, Gift, Pause, Play, Send, Sparkles, Square } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtClock = (s) =>
    `${String(Math.floor(Math.max(0, s) / 60)).padStart(2, "0")}:${String(Math.max(0, s) % 60).padStart(2, "0")}`;

const PRESENCE = ["online", "reading", "away", "offline"];

const LANES = [
    { id: "new", label: "New" },
    { id: "waiting", label: "Waiting" },
    { id: "live", label: "Live Now" },
    { id: "closed", label: "Closed" },
    { id: "archived", label: "Archived" },
];

const inputCls =
    "w-full rounded-xl border border-purple-500/30 bg-[#0b031e]/70 px-4 py-3 text-sm text-[#f8f7ff] placeholder:text-[#c77dff]/40 transition-[border-color,box-shadow] duration-300 focus:border-[#c77dff]/70 focus:outline-none";

const TEMPLATES = {
    yes: {
        explanation:
            "Your sister did not simply receive what was meant for you — she stepped into a fate that was never hers. What should have found you was diverted into her life, leaving the path blocked and the destiny unresolved.",
        next_step:
            "My spiritual work will trace where your paths crossed, break the bond holding your fate in her realm, and draw what was always destined for you back across the mirror.",
    },
    no: {
        explanation:
            "I looked carefully, and the mirror gave no false alarm: this does not carry the signature of a Dichotomy connection. Sometimes life is simply life — painful, unfair, but not stolen.",
        next_step: "Guard your energy. If another pattern ever repeats too precisely to be chance, return to the mirror.",
    },
    unclear: {
        explanation:
            "There are similarities within these two paths, but not enough for me to confirm a Lunar Mirror Sister through the first recognition. The mirror has not fully opened.",
        next_step: "Send me one more memory — the sharpest one — and I will look again.",
    },
};

const RESULT_STYLES = {
    yes: "border-[#c77dff]/70 bg-[#5a189a]/50 text-[#f8f7ff] shadow-[0_0_18px_rgba(199,125,255,0.35)]",
    no: "border-silver/50 bg-silver/10 text-silver",
    unclear: "border-amber-300/50 bg-amber-300/10 text-amber-200",
};

const RecognitionBuilder = ({ conv, headers, onSent }) => {
    const [result, setResult] = useState("yes");
    const [sister, setSister] = useState(conv.sister_name || "");
    const [area, setArea] = useState(conv.diverted_area || "");
    const [diverted, setDiverted] = useState("");
    const [explanation, setExplanation] = useState(TEMPLATES.yes.explanation);
    const [nextStep, setNextStep] = useState(TEMPLATES.yes.next_step);
    const [sending, setSending] = useState(false);

    const pick = (r) => {
        setResult(r);
        setExplanation(TEMPLATES[r].explanation);
        setNextStep(TEMPLATES[r].next_step);
    };

    const submit = async () => {
        setSending(true);
        try {
            await axios.post(
                `${API}/mara/conversations/${conv.id}/recognition`,
                { result, possible_sister: sister, area, diverted, explanation, next_step: nextStep },
                { headers },
            );
            toast.success("Recognition sent through the mirror.");
            onSent();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Recognition refused.");
        } finally {
            setSending(false);
        }
    };

    return (
        <div data-testid="recognition-builder" className="mt-4 rounded-xl border border-[#c77dff]/30 bg-[#18002e]/50 p-5">
            <p className="eyebrow mb-3">Send her recognition</p>
            <div className="flex flex-wrap gap-2">
                {["yes", "no", "unclear"].map((r) => (
                    <button
                        key={r}
                        type="button"
                        onClick={() => pick(r)}
                        data-testid={`builder-result-${r}`}
                        className={`rounded-full border px-5 py-2 font-cinzel text-[11px] tracking-[0.2em] transition-[border-color,box-shadow,background-color,color] duration-300 ${
                            result === r ? RESULT_STYLES[r] : "border-purple-500/25 text-[#e0aaff]/60 hover:text-[#e0aaff]"
                        }`}
                    >
                        {r.toUpperCase()}
                    </button>
                ))}
            </div>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <input value={sister} onChange={(e) => setSister(e.target.value)} data-testid="builder-sister" placeholder="Possible sister" className={inputCls} />
                <input value={area} onChange={(e) => setArea(e.target.value)} data-testid="builder-area" placeholder="Blessing involved" className={inputCls} />
            </div>
            <textarea value={diverted} onChange={(e) => setDiverted(e.target.value)} data-testid="builder-diverted" placeholder="Where the paths reversed…" rows={2} className={`${inputCls} mt-3 resize-none`} />
            <textarea value={explanation} onChange={(e) => setExplanation(e.target.value)} data-testid="builder-explanation" rows={3} className={`${inputCls} mt-3 resize-none`} />
            <textarea value={nextStep} onChange={(e) => setNextStep(e.target.value)} data-testid="builder-next-step" rows={2} className={`${inputCls} mt-3 resize-none`} />
            <button type="button" onClick={submit} disabled={sending} data-testid="builder-send-button" className="btn-glow mt-4 !px-6 !py-2.5 !text-xs disabled:opacity-60">
                SEND RECOGNITION
            </button>
        </div>
    );
};

export const MaraConsole = () => {
    const [pin, setPin] = useState(() => localStorage.getItem("ds_mara_key") || "");
    const [pinInput, setPinInput] = useState("");
    const [authed, setAuthed] = useState(false);
    const [convs, setConvs] = useState([]);
    const [active, setActive] = useState(null);
    const [reply, setReply] = useState("");
    const [comp, setComp] = useState("");
    const [lane, setLane] = useState("new");
    const [orders, setOrders] = useState([]);
    const [bookings, setBookings] = useState([]);
    const [leads, setLeads] = useState([]);
    const [notifyEmail, setNotifyEmail] = useState("");
    const [blocklist, setBlocklist] = useState([]);
    const [blockInput, setBlockInput] = useState("");

    const headers = { "X-Mara-Key": pin };

    const loadConvs = useCallback(async () => {
        if (!pin) return;
        try {
            const res = await axios.get(`${API}/mara/conversations`, { headers });
            setConvs(res.data);
            setAuthed(true);
        } catch {
            setAuthed(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pin]);

    const loadBookings = useCallback(async () => {
        if (!pin) return;
        try {
            const res = await axios.get(`${API}/mara/bookings`, { headers });
            setBookings(res.data);
        } catch { /* ignore */ }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pin]);

    const loadLeads = useCallback(async () => {
        if (!pin) return;
        try {
            const res = await axios.get(`${API}/mara/leads`, { headers });
            setLeads(res.data);
        } catch { /* ignore */ }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pin]);

    useEffect(() => {
        loadConvs();
        loadBookings();
        loadLeads();
        const t = setInterval(() => {
            loadConvs();
            loadBookings();
            loadLeads();
        }, 6000);
        return () => clearInterval(t);
    }, [loadConvs, loadBookings, loadLeads]);

    const openConv = useCallback(
        async (id) => {
            try {
                const res = await axios.get(`${API}/mara/conversations/${id}`, { headers });
                setActive(res.data);
            } catch {
                toast.error("Could not open the conversation.");
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [pin],
    );

    useEffect(() => {
        if (!active) return;
        const id = active.conversation.id;
        const t = setInterval(() => openConv(id), 3000);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active?.conversation?.id, openConv]);

    const loadOrders = useCallback(async () => {
        if (!pin) return;
        try {
            const res = await axios.get(`${API}/mara/orders`, { headers });
            setOrders(res.data);
        } catch {
            // orders stay empty
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pin]);

    useEffect(() => {
        loadOrders();
    }, [loadOrders]);

    const loadAdmin = useCallback(async () => {
        if (!pin) return;
        try {
            const [s, b] = await Promise.all([
                axios.get(`${API}/mara/settings`, { headers }),
                axios.get(`${API}/mara/blocklist`, { headers }),
            ]);
            setNotifyEmail(s.data.notify_email || "");
            setBlocklist(b.data);
        } catch {
            // settings stay empty
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pin]);

    useEffect(() => {
        if (authed) loadAdmin();
    }, [authed, loadAdmin]);

    const saveNotify = async () => {
        try {
            await axios.post(`${API}/mara/settings`, { email: notifyEmail || null }, { headers });
            toast.success(notifyEmail ? "Seeker alerts will find you." : "Seeker alerts silenced.");
        } catch {
            toast.error("The setting refused.");
        }
    };

    const addBlock = async () => {
        if (!blockInput.trim()) return;
        try {
            await axios.post(`${API}/mara/blocklist`, { email: blockInput.trim() }, { headers });
            toast.success("That path is sealed.");
            setBlockInput("");
            loadAdmin();
        } catch {
            toast.error("The seal refused.");
        }
    };

    const removeBlock = async (email) => {
        try {
            await axios.delete(`${API}/mara/blocklist/${encodeURIComponent(email)}`, { headers });
            toast.success("Path reopened.");
            loadAdmin();
        } catch {
            toast.error("Refused.");
        }
    };

    const login = (e) => {
        e.preventDefault();
        localStorage.setItem("ds_mara_key", pinInput);
        setPin(pinInput);
    };

    const toggleArchive = async () => {
        try {
            const res = await axios.post(`${API}/mara/conversations/${active.conversation.id}/archive`, {}, { headers });
            toast.success(res.data.status === "archived" ? "Crossing archived." : "Crossing restored.");
            openConv(active.conversation.id);
            loadConvs();
        } catch {
            toast.error("Refused.");
        }
    };

    const toggleBlock = async () => {
        try {
            const res = await axios.post(`${API}/mara/conversations/${active.conversation.id}/block`, {}, { headers });
            toast.success(res.data.blocked ? "Path sealed." : "Path reopened.");
            openConv(active.conversation.id);
            loadConvs();
        } catch {
            toast.error("Refused.");
        }
    };

    const toggleAi = async () => {
        try {
            const res = await axios.post(
                `${API}/mara/conversations/${active.conversation.id}/ai`,
                { enabled: !active.conversation.ai_active },
                { headers },
            );
            toast.success(res.data.ai_active ? "The echo holds the room while you pull cards." : "You hold the room now.");
            openConv(active.conversation.id);
        } catch {
            toast.error("The toggle refused.");
        }
    };

    const weighBooking = async (id, action) => {
        try {
            const res = await axios.post(`${API}/mara/bookings/${id}/${action}`, {}, { headers });
            if (action === "accept") {
                toast.success(res.data.pay_url ? "Accepted — the payment mirror has been sent to them." : "Accepted — they have been told you will write with payment details.");
            } else if (action === "decline") {
                toast.success("Declined — they have been told gently.");
            } else {
                toast.success("Marked paid — their minutes are in the Back Room.");
            }
            loadBookings();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "The mirror refused.");
        }
    };

    const sessionAction = async (action) => {
        try {
            const res = await axios.post(
                `${API}/mara/conversations/${active.conversation.id}/session/${action}`,
                {},
                { headers },
            );
            toast.success(`Session ${action} — ${fmtClock(res.data.mirror_time.remaining_seconds)} remain.`);
            openConv(active.conversation.id);
        } catch (err) {
            toast.error(err.response?.data?.detail || "The session refused.");
        }
    };

    const setPresence = async (status) => {
        try {
            await axios.post(`${API}/mara/presence`, { status }, { headers });
            toast.success(`You are now ${status}.`);
        } catch {
            toast.error("Presence refused.");
        }
    };

    const addCredit = async () => {
        const minutes = parseInt(comp, 10);
        if (!minutes) return;
        try {
            await axios.post(
                `${API}/mara/conversations/${active.conversation.id}/credit`,
                { minutes },
                { headers },
            );
            toast.success(`Added ${minutes} complimentary minutes.`);
            setComp("");
            openConv(active.conversation.id);
        } catch {
            toast.error("Credit refused.");
        }
    };

    const sendReply = async (e) => {
        e.preventDefault();
        if (!reply.trim()) return;
        try {
            await axios.post(
                `${API}/mara/conversations/${active.conversation.id}/messages`,
                { body: reply.trim() },
                { headers },
            );
            setReply("");
            openConv(active.conversation.id);
        } catch {
            toast.error("Message failed.");
        }
    };

    if (!authed) {
        return (
            <div className="flex min-h-screen items-center justify-center px-5">
                <form onSubmit={login} data-testid="mara-login" className="glass-card w-full max-w-sm p-8 text-center">
                    <img src="/assets/mara-photo.jpg" alt="Mara Vey" className="mx-auto mb-5 h-20 w-20 rounded-full object-cover object-top ring-1 ring-silver/40" />
                    <p className="eyebrow mb-2">Operator only</p>
                    <h1 className="font-display text-xl font-bold text-[#f8f7ff]">MARA'S MIRROR</h1>
                    <input
                        type="password"
                        value={pinInput}
                        onChange={(e) => setPinInput(e.target.value)}
                        data-testid="mara-pin-input"
                        placeholder="Speak the word and enter"
                        className={`${inputCls} mt-6 text-center`}
                    />
                    <button type="submit" data-testid="mara-login-button" className="btn-glow mt-4 w-full">
                        ENTER
                    </button>
                    {pin && <p className="mt-4 text-xs text-[#c77dff]/60">The mirror did not know that word.</p>}
                </form>
            </div>
        );
    }

    const mt = active?.mirror_time;
    const hasRecognition = active?.messages?.some((m) => m.kind === "recognition");

    return (
        <div data-testid="mara-console" className="mx-auto min-h-screen max-w-7xl px-4 py-6 sm:px-6">
            <div className="flex flex-wrap items-center justify-between gap-4 border-b border-purple-500/20 pb-4">
                <div className="flex items-center gap-3">
                    <img src="/assets/mara-photo.jpg" alt="" className="h-10 w-10 rounded-full object-cover object-top ring-1 ring-silver/40" />
                    <h1 className="font-cinzel text-sm tracking-[0.25em] text-[#f8f7ff]">MARA'S MIRROR — OPERATOR</h1>
                </div>
                <div className="flex flex-wrap gap-2">
                    <button
                        type="button"
                        onClick={async () => {
                            try {
                                await axios.post(`${API}/mara/promo/reset`, {}, { headers });
                                toast.success("The free hours begin anew — 24:00:00.");
                            } catch {
                                toast.error("The clock refused.");
                            }
                        }}
                        data-testid="mara-promo-reset"
                        className="rounded-full border border-[#ffd700]/40 px-3 py-1.5 font-cinzel text-[10px] tracking-[0.15em] text-[#ffd700]/80 transition-colors duration-300 hover:bg-[#ffd700]/10"
                    >
                        RESET FREE HOURS
                    </button>
                    {PRESENCE.map((p) => (
                        <button
                            key={p}
                            type="button"
                            onClick={() => setPresence(p)}
                            data-testid={`mara-presence-${p}`}
                            className="rounded-full border border-purple-500/30 px-3 py-1.5 font-cinzel text-[10px] tracking-[0.15em] text-[#e0aaff]/70 transition-colors duration-300 hover:border-[#c77dff]/60 hover:text-[#f8f7ff]"
                        >
                            {p.toUpperCase()}
                        </button>
                    ))}
                </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
                {LANES.map((l) => {
                    const count = convs.filter((c) => c.lane === l.id).length;
                    return (
                        <button
                            key={l.id}
                            type="button"
                            onClick={() => setLane(l.id)}
                            data-testid={`lane-tab-${l.id}`}
                            className={`rounded-full border px-4 py-2 font-cinzel text-[10px] tracking-[0.2em] transition-colors duration-300 ${
                                lane === l.id
                                    ? "border-[#c77dff]/70 bg-[#5a189a]/50 text-[#f8f7ff]"
                                    : "border-purple-500/25 text-[#e0aaff]/60 hover:text-[#e0aaff]"
                            }`}
                        >
                            {l.label.toUpperCase()} · {count}
                        </button>
                    );
                })}
            </div>

            <div className="mt-4 grid gap-6 lg:grid-cols-[1fr_2fr]">
                <div data-testid="mara-conv-list" className="space-y-3">
                    {convs.filter((c) => c.lane === lane).length === 0 && (
                        <p className="font-cormorant text-base italic text-[#e0aaff]/60">Nothing in this lane yet.</p>
                    )}
                    {convs.filter((c) => c.lane === lane).map((c) => (
                        <button
                            key={c.id}
                            type="button"
                            onClick={() => openConv(c.id)}
                            data-testid={`mara-conv-item-${c.id}`}
                            className={`glass-card w-full p-4 text-left transition-[border-color] duration-300 hover:border-[#c77dff]/50 ${
                                active?.conversation?.id === c.id ? "border-[#c77dff]/60" : ""
                            }`}
                        >
                            <div className="flex items-center justify-between gap-2">
                                <p className="font-cinzel text-xs tracking-[0.15em] text-[#f8f7ff]">
                                    {c.customer?.first_name || "Seeker"} · {c.customer?.email}
                                </p>
                                <span className="flex items-center gap-1.5 text-[10px] tracking-wider text-[#c77dff]/60">
                                    {c.kind === "assessment" && <span className="text-[#e0aaff]/80">CUSTOM ·</span>}
                                    {c.blocked && <Ban className="h-3 w-3 text-rose-300" />}
                                    {c.status}
                                </span>
                            </div>
                            <p className="mt-1.5 truncate text-xs text-[#e0aaff]/60">
                                {c.last_message?.kind === "text" ? c.last_message?.body : `[${c.last_message?.kind || "empty"}]`}
                            </p>
                            <p className="mt-1 text-[10px] tracking-wider text-[#c77dff]/50">
                                {c.mirror_time?.state} · {fmtClock(c.mirror_time?.remaining_seconds ?? 0)}
                            </p>
                        </button>
                    ))}
                </div>

                <div>
                    {!active ? (
                        <div className="glass-card flex h-64 items-center justify-center">
                            <p className="font-cormorant text-lg italic text-[#e0aaff]/60">Choose a seeker to open their mirror.</p>
                        </div>
                    ) : (
                        <div className="glass-card p-6" data-testid="mara-active-conv">
                            <div className="flex flex-wrap items-start justify-between gap-4 border-b border-purple-500/15 pb-4">
                                <div>
                                    <p className="font-cinzel text-sm tracking-[0.2em] text-[#f8f7ff]">
                                        {active.customer?.first_name} · {active.customer?.email}
                                    </p>
                                    <p className="mt-1 text-xs text-[#e0aaff]/60">
                                        {active.customer?.country} · {active.customer?.timezone} · Sister:{" "}
                                        {active.conversation.sister_name} ({active.conversation.sister_relationship}) ·{" "}
                                        {active.conversation.diverted_area}
                                    </p>
                                    <p className="mt-2 max-w-xl font-cormorant text-sm italic text-[#e0aaff]/75">
                                        “{active.conversation.story}”
                                    </p>
                                    {active.conversation.kind === "assessment" && (
                                        <p className="mt-1 text-xs tracking-wide text-[#c77dff]/70">
                                            CUSTOM WORKING · ENERGY: {(active.conversation.energy || "unread").toUpperCase()} ·
                                            WANTS: {active.conversation.outcome || "—"}
                                        </p>
                                    )}
                                    {active.conversation.attachments?.length > 0 && (
                                        <div className="mt-2 flex gap-2">
                                            {active.conversation.attachments.map((a, i) => (
                                                <img key={i} src={a} alt="" className="h-14 w-14 rounded-lg border border-purple-500/25 object-cover" />
                                            ))}
                                        </div>
                                    )}
                                    {active.conversation.rating && (
                                        <p data-testid="mara-rating-display" className="mt-2 text-xs tracking-wider text-[#ffd700]/80">
                                            RATED {"★".repeat(active.conversation.rating.stars)}
                                            {"☆".repeat(5 - active.conversation.rating.stars)}
                                            {active.conversation.rating.note ? ` — “${active.conversation.rating.note}”` : ""}
                                        </p>
                                    )}
                                </div>
                                <div className="text-right">
                                    <p data-testid="mara-mirror-time" className="font-mono text-lg text-[#e0aaff]">
                                        {fmtClock(mt?.remaining_seconds ?? 0)}
                                    </p>
                                    <p className="text-[10px] tracking-[0.2em] text-[#c77dff]/60">{mt?.state?.toUpperCase()}</p>
                                </div>
                            </div>

                            <div className="mt-4 flex flex-wrap gap-2">
                                <button type="button" onClick={toggleArchive} data-testid="mara-archive-button" className="flex items-center gap-1.5 rounded-full border border-purple-500/30 px-4 py-2 text-xs text-[#e0aaff]/80 transition-colors hover:bg-[#5a189a]/30">
                                    {active.conversation.status === "archived" ? (
                                        <><ArchiveRestore className="h-3 w-3" /> UNARCHIVE</>
                                    ) : (
                                        <><Archive className="h-3 w-3" /> ARCHIVE</>
                                    )}
                                </button>
                                <button type="button" onClick={toggleBlock} data-testid="mara-block-button" className={`flex items-center gap-1.5 rounded-full border px-4 py-2 text-xs transition-colors ${active.conversation.blocked ? "border-rose-300/50 bg-rose-300/10 text-rose-200" : "border-purple-500/30 text-[#e0aaff]/80 hover:bg-rose-300/10 hover:text-rose-200"}`}>
                                    <Ban className="h-3 w-3" /> {active.conversation.blocked ? "UNBLOCK" : "BLOCK"}
                                </button>
                                <button type="button" onClick={toggleAi} data-testid="mara-ai-toggle" className={`flex items-center gap-1.5 rounded-full border px-4 py-2 text-xs transition-colors ${active.conversation.ai_active ? "border-[#ffd700]/40 bg-[#ffd700]/10 text-[#ffd700]" : "border-purple-500/30 text-[#e0aaff]/80 hover:bg-[#ffd700]/10 hover:text-[#ffd700]"}`}>
                                    <Sparkles className="h-3 w-3" /> {active.conversation.ai_active ? "AI HOLDING — TAKE OVER" : "LET AI HOLD"}
                                </button>
                            </div>

                            <div className="mt-3 flex flex-wrap gap-2">
                                <button type="button" onClick={() => sessionAction("start")} data-testid="mara-session-start" className="flex items-center gap-1.5 rounded-full border border-emerald-300/40 px-4 py-2 text-xs text-emerald-200 transition-colors hover:bg-emerald-300/10">
                                    <Play className="h-3 w-3" /> START
                                </button>
                                <button type="button" onClick={() => sessionAction("pause")} data-testid="mara-session-pause" className="flex items-center gap-1.5 rounded-full border border-amber-300/40 px-4 py-2 text-xs text-amber-200 transition-colors hover:bg-amber-300/10">
                                    <Pause className="h-3 w-3" /> PAUSE
                                </button>
                                <button type="button" onClick={() => sessionAction("resume")} data-testid="mara-session-resume" className="flex items-center gap-1.5 rounded-full border border-[#c77dff]/40 px-4 py-2 text-xs text-[#e0aaff] transition-colors hover:bg-[#c77dff]/10">
                                    <Play className="h-3 w-3" /> RESUME
                                </button>
                                <button type="button" onClick={() => sessionAction("finish")} data-testid="mara-session-finish" className="flex items-center gap-1.5 rounded-full border border-rose-300/40 px-4 py-2 text-xs text-rose-200 transition-colors hover:bg-rose-300/10">
                                    <Square className="h-3 w-3" /> FINISH
                                </button>
                                <div className="ml-auto flex items-center gap-2">
                                    <input
                                        type="number"
                                        min="1"
                                        value={comp}
                                        onChange={(e) => setComp(e.target.value)}
                                        data-testid="mara-credit-input"
                                        placeholder="min"
                                        className="w-20 rounded-full border border-purple-500/30 bg-[#0b031e]/70 px-3 py-2 text-xs text-[#f8f7ff] focus:outline-none"
                                    />
                                    <button type="button" onClick={addCredit} data-testid="mara-credit-add" className="flex items-center gap-1.5 rounded-full border border-[#e0aaff]/40 px-4 py-2 text-xs text-[#e0aaff] transition-colors hover:bg-[#e0aaff]/10">
                                        <Gift className="h-3 w-3" /> GIFT
                                    </button>
                                </div>
                            </div>

                            {!hasRecognition && (
                                <RecognitionBuilder
                                    key={active.conversation.id}
                                    conv={active.conversation}
                                    headers={headers}
                                    onSent={() => openConv(active.conversation.id)}
                                />
                            )}

                            <div data-testid="mara-messages" className="mt-5 max-h-[26rem] space-y-4 overflow-y-auto rounded-xl border border-purple-500/15 bg-[#06010f]/60 p-4">
                                {active.messages.map((m) =>
                                    m.kind === "recognition" ? (
                                        <div key={m.id} className="rounded-xl border border-[#c77dff]/30 bg-[#18002e]/60 p-4 text-xs text-[#e0aaff]/80">
                                            RECOGNITION SENT: {m.meta?.result?.toUpperCase()} — {m.meta?.possible_sister} · {m.meta?.area}
                                        </div>
                                    ) : (
                                        <div key={m.id} className={`flex ${m.sender === "mara" ? "justify-end" : "justify-start"}`}>
                                            <div
                                                className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${
                                                    m.sender === "mara"
                                                        ? "rounded-br-sm border border-[#c77dff]/40 bg-[#5a189a]/50 text-[#f8f7ff]"
                                                        : "rounded-bl-sm border border-silver/25 bg-[#0b031e]/70 text-[#f8f7ff]/90"
                                                }`}
                                            >
                                                {m.sender === "mara" && m.meta?.ai && (
                                                    <span data-testid="mara-ai-badge" className="mb-1 block text-[9px] tracking-[0.25em] text-[#ffd700]/70">
                                                        AI ECHO — HOLDING THE ROOM
                                                    </span>
                                                )}
                                                {m.kind === "image" ? (
                                                    <img src={m.body} alt="customer upload" className="max-h-48 rounded-lg" />
                                                ) : m.kind === "voice" ? (
                                                    <div>
                                                        <audio controls src={m.body} className="w-52" />
                                                        {m.meta?.transcript && (
                                                            <p
                                                                data-testid={`mara-voice-transcript-${m.id}`}
                                                                className="mt-1.5 font-cormorant text-xs italic text-[#e0aaff]/80"
                                                            >
                                                                "{m.meta.transcript}"
                                                            </p>
                                                        )}
                                                    </div>
                                                ) : (
                                                    m.body
                                                )}
                                            </div>
                                        </div>
                                    ),
                                )}
                            </div>

                            <form onSubmit={sendReply} className="mt-4 flex gap-2">
                                <input
                                    type="text"
                                    value={reply}
                                    onChange={(e) => setReply(e.target.value)}
                                    data-testid="mara-reply-input"
                                    placeholder="Speak as Mara…"
                                    className={inputCls}
                                />
                                <button type="submit" data-testid="mara-send-button" aria-label="Send" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#c77dff]/50 bg-[#5a189a]/60 text-[#f8f7ff] transition-transform active:scale-95">
                                    <Send className="h-4 w-4" />
                                </button>
                            </form>
                        </div>
                    )}
                </div>
            </div>

            {bookings.length > 0 && (
                <div className="mt-10" data-testid="mara-bookings">
                    <p className="eyebrow mb-4">Bookings awaiting your word</p>
                    <div className="grid gap-4 md:grid-cols-2">
                        {[...bookings]
                            .sort((a, b) => (a.status === "pending" ? 0 : 1) - (b.status === "pending" ? 0 : 1))
                            .map((b) => (
                                <div key={b.id} data-testid={`booking-card-${b.id.slice(-6)}`} className={`glass-card p-5 ${b.status === "declined" ? "opacity-50" : ""}`}>
                                    <div className="flex items-start justify-between gap-3">
                                        <div>
                                            <p className="font-cinzel text-xs tracking-[0.15em] text-[#f8f7ff]">
                                                {(b.name || "Seeker").toUpperCase()} · {b.email}
                                            </p>
                                            <p className="mt-1 text-xs text-[#e0aaff]/70">
                                                {b.reading_title} · ${(b.amount || 0).toFixed(2)}
                                                {b.minutes > 0 && ` · ${b.minutes} min`}
                                            </p>
                                        </div>
                                        <span
                                            data-testid={`booking-status-${b.id.slice(-6)}`}
                                            className={`shrink-0 rounded-full border px-3 py-1 font-cinzel text-[9px] tracking-[0.2em] ${
                                                b.status === "pending"
                                                    ? "border-[#ffd700]/40 text-[#ffd700]"
                                                    : b.status === "paid"
                                                      ? "border-emerald-300/40 text-emerald-200"
                                                      : b.status === "accepted"
                                                        ? "border-[#c77dff]/40 text-[#c77dff]"
                                                        : "border-purple-500/30 text-[#e0aaff]/50"
                                            }`}
                                        >
                                            {b.status === "accepted" ? "AWAITING PAYMENT" : (b.status || "pending").toUpperCase()}
                                        </span>
                                    </div>
                                    {b.preferred_time && <p className="mt-2 text-[11px] text-[#c77dff]/70">When: {b.preferred_time}</p>}
                                    {b.intention && <p className="mt-1 font-cormorant text-sm italic text-[#e0aaff]/75">"{b.intention}"</p>}
                                    {(b.birth_date || b.birth_place) && (
                                        <p className="mt-1 text-[10px] text-[#c77dff]/50">
                                            {[b.birth_date, b.birth_time, b.birth_place].filter(Boolean).join(" · ")}
                                        </p>
                                    )}
                                    {b.status === "pending" && (
                                        <div className="mt-4 flex gap-2">
                                            <button
                                                type="button"
                                                onClick={() => weighBooking(b.id, "accept")}
                                                data-testid={`mara-booking-accept-${b.id.slice(-6)}`}
                                                className="flex-1 rounded-full border border-[#ffd700]/40 bg-[#ffd700]/10 px-4 py-2 font-cinzel text-[10px] tracking-[0.2em] text-[#ffd700] transition-colors hover:bg-[#ffd700]/20"
                                            >
                                                ACCEPT
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => weighBooking(b.id, "decline")}
                                                data-testid={`mara-booking-decline-${b.id.slice(-6)}`}
                                                className="flex-1 rounded-full border border-purple-500/30 px-4 py-2 font-cinzel text-[10px] tracking-[0.2em] text-[#e0aaff]/70 transition-colors hover:bg-rose-300/10 hover:text-rose-200"
                                            >
                                                DECLINE
                                            </button>
                                        </div>
                                    )}
                                    {b.status === "accepted" && (
                                        <button
                                            type="button"
                                            onClick={() => weighBooking(b.id, "mark-paid")}
                                            data-testid={`mara-booking-paid-${b.id.slice(-6)}`}
                                            className="mt-4 w-full rounded-full border border-emerald-300/40 bg-emerald-300/10 px-4 py-2 font-cinzel text-[10px] tracking-[0.2em] text-emerald-200 transition-colors hover:bg-emerald-300/20"
                                        >
                                            MARK PAID — GIVE THEIR MINUTES
                                        </button>
                                    )}
                                    <p className="mt-2 text-[10px] text-[#c77dff]/40">{new Date(b.created_at).toLocaleString()}</p>
                                </div>
                            ))}
                    </div>
                </div>
            )}

            {leads.length > 0 && (
                <div className="mt-10" data-testid="mara-leads">
                    <p className="eyebrow mb-4">Mirror leads — what they asked the oracle</p>
                    <div className="grid gap-4 md:grid-cols-2">
                        {leads.map((l) => (
                            <div key={l.id} data-testid={`mara-lead-${l.id.slice(-6)}`} className="glass-card p-5">
                                <div className="flex items-start justify-between gap-3">
                                    <p className="font-cinzel text-xs tracking-[0.15em] text-[#f8f7ff]">
                                        {(l.name || "Seeker").toUpperCase()} · {l.email}
                                    </p>
                                    {l.marketing_opt_in && (
                                        <span className="shrink-0 rounded-full border border-[#ffd700]/40 px-3 py-1 font-cinzel text-[9px] tracking-[0.2em] text-[#ffd700]">
                                            MARKETING
                                        </span>
                                    )}
                                </div>
                                <p className="mt-2 font-cormorant text-sm italic text-[#c77dff]/80">"{l.question}"</p>
                                <p className="mt-1 text-[11px] leading-relaxed text-[#e0aaff]/60">{l.answer}</p>
                                <p className="mt-2 text-[10px] text-[#c77dff]/40">{new Date(l.created_at).toLocaleString()}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {orders.length > 0 && (
                <div className="mt-10" data-testid="mara-orders">
                    <p className="eyebrow mb-4">Ritual orders</p>
                    <div className="grid gap-4 md:grid-cols-2">
                        {orders.map((o) => (
                            <div key={o.session_id} data-testid={`order-card-${o.session_id.slice(-6)}`} className="glass-card p-5">
                                <p className="font-cinzel text-xs tracking-[0.15em] text-[#f8f7ff]">{o.product || "Ritual object"}</p>
                                <p className="mt-1 text-xs text-[#e0aaff]/70">
                                    ${(o.amount || 0).toFixed(2)} · {o.customer_email || "email unknown"}
                                </p>
                                {o.shipping && (
                                    <p className="mt-1 text-[11px] text-[#c77dff]/60">
                                        {o.shipping.name} —{" "}
                                        {[o.shipping.address?.line1, o.shipping.address?.city, o.shipping.address?.country]
                                            .filter(Boolean)
                                            .join(", ")}
                                    </p>
                                )}
                                <p className="mt-1 text-[10px] text-[#c77dff]/40">{new Date(o.created_at).toLocaleString()}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="mt-10 grid gap-6 md:grid-cols-2" data-testid="mara-settings">
                <div className="glass-card p-5">
                    <p className="eyebrow mb-3">Seeker alerts</p>
                    <p className="mb-3 text-xs text-[#e0aaff]/60">Email me the moment a new seeker enters.</p>
                    <div className="flex gap-2">
                        <input type="email" value={notifyEmail} onChange={(e) => setNotifyEmail(e.target.value)} data-testid="mara-notify-email" placeholder="your@email.com" className={inputCls} />
                        <button type="button" onClick={saveNotify} data-testid="mara-notify-save" className="btn-ghost-glow shrink-0 !px-5 !py-2.5">SAVE</button>
                    </div>
                </div>
                <div className="glass-card p-5">
                    <p className="eyebrow mb-3">Sealed paths</p>
                    <p className="mb-3 text-xs text-[#e0aaff]/60">Sealed emails can never open the mirror.</p>
                    <div className="flex gap-2">
                        <input type="email" value={blockInput} onChange={(e) => setBlockInput(e.target.value)} data-testid="mara-block-input" placeholder="email to seal" className={inputCls} />
                        <button type="button" onClick={addBlock} data-testid="mara-block-add" className="btn-ghost-glow shrink-0 !border-rose-300/40 !px-5 !py-2.5 !text-rose-200">SEAL</button>
                    </div>
                    {blocklist.length > 0 && (
                        <ul className="mt-3 space-y-1.5">
                            {blocklist.map((b) => (
                                <li key={b.email} className="flex items-center justify-between text-xs text-[#e0aaff]/70">
                                    <span>{b.email}</span>
                                    <button type="button" onClick={() => removeBlock(b.email)} data-testid={`mara-unblock-${b.email}`} className="text-[#c77dff]/60 transition-colors hover:text-[#f8f7ff]">reopen</button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
};
